import { Box, Grid, Stack } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { AmbassadorProfileContext } from "..";
import { deleteProfileProduct, getUserProfile } from "../../../../api/profile";
import { AppButton } from "../../../../components/form";
import ModalBox from "../../../../components/modal";
import { useMutationWithDispatch } from "../../../../hooks/useMutationWithDispatch";
import useToggle from "../../../../hooks/useToggle";
import { useAppDispatch } from "../../../../store";
import MyProfileSectionTitle from "../section-title";
import AddOrUpdateProduct from "./add-or-update";
import ProductBlock from "./product-block";
import TechnologyStackFilter from "./technology-stack-filter";

const SHOW_LESS_DATA_LENGTH = 6;

function ProductList() {
  const [openEditModal, setOpenEditModal] = useToggle(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { data, editMode, loading } = useContext(AmbassadorProfileContext);
  const [showMore, setShowMore] = useState(
    data?.technologies?.length > SHOW_LESS_DATA_LENGTH ? true : false
  );
  const [filteredList, setFilteredList] = useState([data?.technologies]);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const dispatch = useAppDispatch();

  const { mutateAsync: deleteProduct } = useMutationWithDispatch(
    (variables: { id: string }) => deleteProfileProduct(variables.id),
    { showErrorToast: true }
  );

  useEffect(() => {
    selectedProduct && setOpenEditModal();
  }, [selectedProduct]);

  useEffect(() => {
    setFilteredList(data?.technologies);
  }, [data]);

  const onSubmit = () => {
    setOpenEditModal();
    setSelectedProduct(null);
    dispatch(getUserProfile());
  };

  const onDelete = async (product) => {
    setDeletingProductId(product.id);
    try {
      const res = await deleteProduct({ id: product.id });
      if (res?.success) {
        dispatch(getUserProfile());
      }
    } finally {
      setDeletingProductId(null);
    }
  };

  const onClose = () => {
    setOpenEditModal();
    setSelectedProduct(null);
  };

  const handleFilterChange = (list) => {
    if (!list?.length) {
      setFilteredList(data?.technologies);
    } else {
      setFilteredList(
        data?.technologies?.filter((i) => list?.includes(i?.category))
      );
    }
  };

  if (!editMode && !data?.technologies?.length) return null;

  return (
    <Stack gap={2}>
      <MyProfileSectionTitle
        title={"Your Cybersecurity Technology Stack"}
        sx={{ textTransform: "uppercase" }}
        subtitle={""}
        onAddClick={setOpenEditModal}
        editable={false}
        operation={"ADD"}
      />

      <Grid container spacing={2}>
        {filteredList?.length > 0 && (
          <Grid item xs={12} mb={1}>
            <TechnologyStackFilter onFilterChange={handleFilterChange} />
          </Grid>
        )}

        <Grid item spacing={2} container>
          {filteredList &&
            filteredList?.length > 0 &&
            filteredList
              ?.map((product, productIndex) => (
                <Grid item xs={12} md={4} key={productIndex}>
                  <ProductBlock
                    data={product}
                    editable={editMode}
                    onEditClick={() => setSelectedProduct(product)}
                    onDeleteClick={() => onDelete(product)}
                    deleteLoading={deletingProductId === product.id}
                  />
                </Grid>
              ))
              .slice(
                0,
                showMore ? SHOW_LESS_DATA_LENGTH : filteredList.length + 1
              )}
        </Grid>
      </Grid>

      <Box
        display={"flex"}
        mt={2}
        justifyContent="center"
        alignItems={"center"}
      >
        {showMore && (
          <AppButton
            onClick={() => setShowMore(!showMore)}
            fullWidth={false}
            style={{ height: 31 }}
            color="secondary"
          >
            show more
          </AppButton>
        )}
      </Box>

      {openEditModal && (
        <ModalBox title={"New Product"} onClose={onClose} showFooter={false}>
          <AddOrUpdateProduct
            key={selectedProduct?.id ?? "new"}
            onSubmit={onSubmit}
            onCancel={onClose}
            data={selectedProduct}
          />
        </ModalBox>
      )}
    </Stack>
  );
}

export default ProductList;
